// ============================================
// App Layout Component - Based on approved views/dashboard.html
// ============================================
// Main layout with sidebar navigation and mobile bottom nav

import { ReactNode, useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  Building2,
  Home,
  LogOut,
  Menu,
  Plus,
  Search,
  Settings,
  Shield,
  Target,
  UserCheck,
  UserPlus,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCommandSearchStore } from '@/stores';
import { AuthGuard } from './AuthGuard';
import { CommandSearch } from './CommandSearch';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Toaster } from '@/components/feedback/Toaster';
import { TenantSwitcher } from './TenantSwitcher';
import { useTenant } from '@/hooks/useTenant';

interface AppLayoutProps {
  children: ReactNode;
}

type MatchMode = 'exact' | 'startsWith';

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  matchMode?: MatchMode;
}

const primaryNavItems: NavItem[] = [
  {
    href: '/',
    label: 'Dashboard',
    icon: Home,
    matchMode: 'exact',
  },
  {
    href: '/leads',
    label: 'Leads',
    icon: UserPlus,
  },
  {
    href: '/accounts',
    label: 'Accounts',
    icon: Building2,
  },
  {
    href: '/contacts',
    label: 'Contacts',
    icon: Users,
  },
  {
    href: '/opportunities',
    label: 'Opportunities',
    icon: Target,
  },
];

const settingsItem: NavItem = {
  href: '/settings',
  label: 'Settings',
  icon: Settings,
};

const adminDashboardItem: NavItem = {
  href: '/admin/dashboard',
  label: 'Admin Dashboard',
  icon: Shield,
};

const tenantAdminItems: NavItem[] = [
  {
    href: '/admin/tenants',
    label: 'Tenants',
    icon: Building2,
    matchMode: 'exact',
  },
  {
    href: '/admin/tenants/users',
    label: 'Tenant Users',
    icon: UserCheck,
  },
  {
    href: '/admin/tenants/onboarding',
    label: 'Onboarding',
    icon: Plus,
  },
];

function getInitials(name: string): string {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout, isLoggingOut } = useAuth();
  const { mode, activeTenant, brandColor } = useTenant();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const openSearch = useCommandSearchStore(state => state.open);

  const isActive = (href: string, matchMode: MatchMode = 'startsWith') => {
    if (matchMode === 'exact') {
      return location.pathname === href;
    }

    if (href === '/') {
      return location.pathname === '/';
    }

    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const userInitials = user?.name ? getInitials(user.name) : 'U';
  const tenantLabel = mode === 'all' ? 'All tenants' : activeTenant?.name || 'Tenant not selected';
  const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';

  return (
    <AuthGuard>
      <div className="flex h-screen overflow-hidden bg-muted">
        {/* Skip to content link for accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Skip to content
        </a>

        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col w-64 border-r border-border bg-card">
          {/* Logo */}
          <div className="flex items-center gap-3 h-16 px-4 border-b border-border">
            <img src="/logo.svg" alt="Muraka CRM" className="h-8 w-8" />
            <span className="font-semibold text-foreground">Muraka CRM</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1">
            {primaryNavItems.map(item => {
              const active = isActive(item.href, item.matchMode);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  {item.label}
                </Link>
              );
            })}

            <div className="my-4 border-t border-border" />

            {/* Settings */}
            <Link
              to={settingsItem.href}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isActive(settingsItem.href, settingsItem.matchMode)
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <settingsItem.icon className="w-5 h-5 shrink-0" />
              {settingsItem.label}
            </Link>

            {isAdminUser && (
              <>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Admin
                  </p>
                  <Link
                    to={adminDashboardItem.href}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isActive(adminDashboardItem.href, adminDashboardItem.matchMode)
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    <adminDashboardItem.icon className="w-5 h-5 shrink-0" />
                    {adminDashboardItem.label}
                  </Link>
                </div>

                <div className="mt-2">
                  <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Tenant Management
                  </p>
                  {tenantAdminItems.map(item => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn(
                        'ml-3 flex items-center gap-3 rounded-lg border-l border-border/80 px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isActive(item.href, item.matchMode)
                          ? 'bg-primary text-primary-foreground font-medium border-l-primary/60'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </nav>

          {/* User Section */}
          <div className="p-3 border-t border-border">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">{userInitials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                  {brandColor && (
                    <span
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: brandColor }}
                    />
                  )}
                  {tenantLabel}
                </p>
              </div>
              <button
                onClick={() => logout()}
                disabled={isLoggingOut}
                className="p-1 rounded text-muted-foreground hover:text-foreground"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="border-b border-border bg-card shrink-0">
            {brandColor && (
              <div
                className="h-[3px] w-full"
                style={{ backgroundColor: brandColor }}
                data-testid="tenant-brand-bar"
                role="presentation"
                aria-hidden="true"
              />
            )}
            <div className="h-16 flex items-center justify-between px-4 lg:px-6">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground"
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* Mobile Logo */}
              <div className="lg:hidden flex items-center gap-2">
                <img src="/logo.svg" alt="Muraka CRM" className="h-7 w-7" />
                <span className="font-semibold text-foreground">Muraka</span>
              </div>

              {/* Search (Desktop) */}
              <div className="hidden lg:flex items-center flex-1 max-w-md">
                <CommandSearch />
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-2">
                <TenantSwitcher />

                {/* Mobile Search */}
                <button
                  onClick={openSearch}
                  className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
                >
                  <Search className="w-5 h-5" />
                </button>

                {/* User Avatar (Desktop only) */}
                <div className="hidden lg:flex items-center gap-2 ml-2 pl-2 border-l border-border">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">{userInitials}</span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main id="main-content" className="flex-1 overflow-auto p-4 lg:p-6" tabIndex={-1}>
            {children}
          </main>
        </div>

        {/* Mobile Bottom Navigation */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border safe-area-bottom z-40">
          <div className="flex items-center justify-around">
            {primaryNavItems.slice(0, 4).map(item => {
              const active = isActive(item.href, item.matchMode);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex flex-col items-center gap-1 py-2 px-2 min-w-[56px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md',
                    active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium">
                    {item.label === 'Dashboard'
                      ? 'Home'
                      : item.label === 'Opportunities'
                        ? 'Pipeline'
                        : item.label}
                  </span>
                </Link>
              );
            })}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="flex flex-col items-center gap-1 py-2 px-2 text-muted-foreground hover:text-foreground min-w-[56px]"
            >
              <Menu className="w-5 h-5" />
              <span className="text-[10px] font-medium">More</span>
            </button>
          </div>
        </nav>

        {/* Spacer for mobile bottom nav */}
        <div className="lg:hidden h-20" />

        {/* Mobile Slide-Out Menu Overlay */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-50"
            onClick={() => setMobileMenuOpen(false)}
          >
            {/* Slide-Out Panel */}
            <div
              className="absolute top-0 left-0 h-full w-80 max-w-[85vw] bg-card shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between h-16 px-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <img src="/logo.svg" alt="Muraka CRM" className="h-8 w-8" />
                  <span className="font-semibold text-foreground">Muraka CRM</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Menu Navigation */}
              <nav className="p-3 space-y-1">
                {primaryNavItems.map(item => {
                  const active = isActive(item.href, item.matchMode);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        active
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <item.icon className="w-5 h-5 shrink-0" />
                      {item.label}
                    </Link>
                  );
                })}
                <div className="my-4 border-t border-border" />
                <Link
                  to={settingsItem.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive(settingsItem.href, settingsItem.matchMode)
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <settingsItem.icon className="w-5 h-5 shrink-0" />
                  {settingsItem.label}
                </Link>

                {isAdminUser && (
                  <>
                    <div className="my-2 border-t border-border" />
                    <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Admin
                    </p>
                    <Link
                      to={adminDashboardItem.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isActive(adminDashboardItem.href, adminDashboardItem.matchMode)
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <adminDashboardItem.icon className="w-5 h-5 shrink-0" />
                      {adminDashboardItem.label}
                    </Link>
                    <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Tenant Management
                    </p>
                    {tenantAdminItems.map(item => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          'ml-3 flex items-center gap-3 rounded-lg border-l border-border/80 px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          isActive(item.href, item.matchMode)
                            ? 'bg-primary text-primary-foreground font-medium border-l-primary/60'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}
                      >
                        <item.icon className="w-4 h-4 shrink-0" />
                        {item.label}
                      </Link>
                    ))}
                  </>
                )}
              </nav>

              {/* User Section */}
              <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-border bg-card">
                <div className="flex items-center gap-3 px-3 py-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">{userInitials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => logout()}
                    disabled={isLoggingOut}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        <Toaster />
      </div>
    </AuthGuard>
  );
}
